import { Button } from "@mui/material";

import { AssignmentResponse, AssignmentStatus } from "@/api";
import { FormDialog } from "@/common/components/form-dialog/FormDialog";
import { useTranslation } from "@/translations";

import { AssignmentFormFields } from "../shared/AssignmentFormFields";
import { useAssignmentEdit } from "./hooks/useAssignmentEdit";

interface Props {
  assignment: AssignmentResponse;
  onClose: () => void;
}

export function AssignmentEditDialog({ assignment, onClose }: Props) {
  const { t } = useTranslation();
  const { formik, priorityOptions, handleDelete, isDeleting, handleToggleStatus, isTogglingStatus } = useAssignmentEdit(
    assignment,
    onClose
  );

  // A completed assignment (Aufgabe) is shown for reading only: it can be reopened or deleted, but not edited.
  const isCompleted = assignment.status === AssignmentStatus.Completed;

  return (
    <FormDialog
      title={t("assignments.editDialog.title")}
      onClose={onClose}
      formik={formik}
      submitLabel={isCompleted ? undefined : t("common.save")}
      fullWidth
      extraAction={{
        left: (
          <Button
            variant="outlined"
            color="error"
            onClick={handleDelete}
            disabled={isDeleting || formik.isSubmitting || isTogglingStatus}
          >
            {t("assignments.editDialog.deleteButton")}
          </Button>
        ),
        right: (
          <Button
            variant="outlined"
            color="primary"
            onClick={handleToggleStatus}
            disabled={isTogglingStatus || isDeleting || formik.isSubmitting}
          >
            {isCompleted ? t("assignments.markOpen") : t("assignments.markCompleted")}
          </Button>
        ),
      }}
    >
      <AssignmentFormFields
        formik={formik}
        priorityOptions={priorityOptions}
        clientId={assignment.assignedClientId}
        clientName={assignment.assignedClientFullName}
        clientReadOnly
        caseNoteId={assignment.assignedCaseNoteId}
        caseNoteLabel={assignment.assignedCaseNoteLabel}
        readOnly={isCompleted}
      />
    </FormDialog>
  );
}
